/**
 * Process Runner Component.
 *
 * Renders an active process instance with all its steps. Used on
 * WO Detail and Asset Detail pages. Allows users to complete steps
 * one by one with rich field-type rendering.
 */

import { useState } from "react";
import {
  CheckCircle,
  Circle,
  AlertTriangle,
  Camera,
  PenTool,
  Hash,
  Calendar,
  DollarSign,
  List,
  ChevronDown,
  ChevronRight,
  CheckSquare,
  Type,
  ToggleLeft,
  Gauge,
  FileText,
  Heading,
  Play,
  X,
  Check,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface StepCompletion {
  id: string;
  instance_id: string;
  step_id: string;
  sort_order: number;
  step_type: string;
  label: string;
  value: Record<string, unknown> | null;
  is_completed: boolean;
  completed_at: string | null;
  completed_by: string | null;
  score_earned: number;
  notes: string | null;
}

interface ProcessInstance {
  id: string;
  template_id: string;
  template_name: string;
  template_version: number;
  entity_type: string;
  entity_id: string;
  status: string;
  started_by: string | null;
  started_at: string;
  completed_at: string | null;
  completed_by: string | null;
  total_steps: number;
  completed_steps: number;
  score: number | null;
  score_passed: boolean | null;
  notes: string | null;
  step_completions: StepCompletion[];
}

interface ProcessTemplate {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  icon: string | null;
  entity_type: string;
  status: string;
  scoring_enabled: boolean;
  estimated_minutes: number | null;
  step_count: number;
}

const STEP_ICONS: Record<string, React.ElementType> = {
  checkbox: CheckSquare,
  checklist: List,
  text: Type,
  number: Hash,
  amount: DollarSign,
  select: ToggleLeft,
  multiselect: List,
  inspection: AlertTriangle,
  yes_no_na: ToggleLeft,
  photo: Camera,
  signature: PenTool,
  date: Calendar,
  meter_reading: Gauge,
  heading: Heading,
  instruction: FileText,
};

const NON_INPUT_TYPES = new Set(["heading", "instruction"]);

interface ProcessRunnerProps {
  entityType: string;
  entityId: string;
}

function ProcessRunner({ entityType, entityId }: ProcessRunnerProps) {
  const qc = useQueryClient();

  // Fetch available templates.
  const { data: templates } = useQuery<ProcessTemplate[]>({
    queryKey: ["process-templates", entityType],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/processes/templates?entity_type=${entityType}&status=published`,
        )
      ).data,
  });

  // Fetch active instances for this entity.
  const { data: instances, isLoading } = useQuery<ProcessInstance[]>({
    queryKey: ["process-instances", entityType, entityId],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/processes/instances?entity_type=${entityType}&entity_id=${entityId}`,
        )
      ).data,
  });

  const startMutation = useMutation({
    mutationFn: async (templateId: string) =>
      (
        await apiClient.post("/api/v1/processes/instances", {
          template_id: templateId,
          entity_type: entityType,
          entity_id: entityId,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["process-instances", entityType, entityId],
      });
      toast.success("Process started");
    },
    onError: () => toast.error("Failed to start process"),
  });

  const [expandedInstance, setExpandedInstance] = useState<string | null>(null);

  if (isLoading) return <LoadingSpinner className="py-4" />;

  const activeInstances = (instances || []).filter(
    (i) => i.status === "in_progress",
  );
  const completedInstances = (instances || []).filter(
    (i) => i.status !== "in_progress",
  );

  return (
    <div className="space-y-4">
      {/* Start new process */}
      {templates && templates.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {templates.map((t) => (
            <button
              key={t.id}
              onClick={() => startMutation.mutate(t.id)}
              className="flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--border-default)] px-3 py-2 text-xs font-medium text-[var(--text-secondary)] transition-all hover:border-[var(--color-interactive-primary)]/30 hover:text-[var(--color-interactive-primary)] hover:bg-[var(--color-interactive-primary)]/5"
            >
              <Play className="h-3.5 w-3.5" />
              {t.icon && <span>{t.icon}</span>}
              {t.name}
              {t.estimated_minutes && (
                <span className="text-[var(--text-tertiary)]">
                  ~{t.estimated_minutes}m
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Active instances */}
      {activeInstances.map((instance) => (
        <ActiveProcessCard
          key={instance.id}
          instance={instance}
          isExpanded={expandedInstance === instance.id}
          onToggle={() =>
            setExpandedInstance(
              expandedInstance === instance.id ? null : instance.id,
            )
          }
        />
      ))}

      {/* Completed instances (collapsed) */}
      {completedInstances.length > 0 && (
        <div>
          <p className="text-[9px] font-medium uppercase tracking-[0.1em] text-[var(--text-tertiary)] mb-2">
            Completed
          </p>
          <div className="space-y-1">
            {completedInstances.slice(0, 5).map((instance) => (
              <div
                key={instance.id}
                className="flex items-center justify-between rounded-[var(--radius-md)] border border-[var(--border-subtle)] px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-[var(--text-success)]" />
                  <span className="text-sm text-[var(--text-primary)]">
                    {instance.template_name}
                  </span>
                  <Badge
                    variant={
                      instance.status === "completed" ? "success" : "default"
                    }
                  >
                    {instance.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-xs text-[var(--text-tertiary)]">
                  {instance.score !== null && (
                    <Badge
                      variant={instance.score_passed ? "success" : "danger"}
                    >
                      {instance.score}%
                    </Badge>
                  )}
                  <span>
                    {new Date(
                      instance.completed_at || instance.started_at,
                    ).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!instances?.length && !templates?.length && (
        <p className="text-center text-xs text-[var(--text-tertiary)] py-4">
          No processes available
        </p>
      )}
    </div>
  );
}

/** Active process card with expandable step list */
function ActiveProcessCard({
  instance,
  isExpanded,
  onToggle,
}: {
  instance: ProcessInstance;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const qc = useQueryClient();
  const progress =
    instance.total_steps > 0
      ? (instance.completed_steps / instance.total_steps) * 100
      : 0;

  const completeMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post(
          `/api/v1/processes/instances/${instance.id}/complete`,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process-instances"] });
      toast.success("Process completed");
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post(
          `/api/v1/processes/instances/${instance.id}/cancel`,
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process-instances"] });
      toast.success("Process cancelled");
    },
  });

  return (
    <Card padding="none">
      {/* Header */}
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-[var(--text-tertiary)]" />
        ) : (
          <ChevronRight className="h-4 w-4 text-[var(--text-tertiary)]" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[var(--text-primary)]">
              {instance.template_name}
            </span>
            <Badge variant="warning">In progress</Badge>
            <span className="text-xs text-[var(--text-tertiary)]">
              {instance.completed_steps}/{instance.total_steps} steps
            </span>
          </div>
          {/* Progress bar */}
          <div className="mt-1.5 h-1.5 rounded-full bg-[var(--border-subtle)] overflow-hidden max-w-xs">
            <div
              className="h-full rounded-full bg-[var(--color-interactive-primary)] transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              completeMutation.mutate();
            }}
          >
            <Check className="h-3.5 w-3.5" /> Complete
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              cancelMutation.mutate();
            }}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </button>

      {/* Expanded step list */}
      {isExpanded && (
        <div className="border-t border-[var(--border-subtle)] px-4 py-3 space-y-2">
          {instance.step_completions.map((sc) => (
            <StepRow key={sc.id} stepCompletion={sc} instanceId={instance.id} />
          ))}
        </div>
      )}
    </Card>
  );
}

/** Individual step row with inline completion */
function StepRow({
  stepCompletion: sc,
  instanceId,
}: {
  stepCompletion: StepCompletion;
  instanceId: string;
}) {
  const qc = useQueryClient();
  const [value, setValue] = useState<string>("");
  const [showInput, setShowInput] = useState(false);
  const Icon = STEP_ICONS[sc.step_type] || Circle;
  const isNonInput = NON_INPUT_TYPES.has(sc.step_type);

  const completeMutation = useMutation({
    mutationFn: async (val: unknown) =>
      (
        await apiClient.put(
          `/api/v1/processes/instances/${instanceId}/steps/${sc.id}`,
          { value: val },
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["process-instances"] });
      setShowInput(false);
      setValue("");
    },
    onError: () => toast.error("Failed to save step"),
  });

  // Heading / instruction — non-interactive
  if (isNonInput) {
    return (
      <div
        className={`flex items-center gap-3 py-1 ${sc.step_type === "heading" ? "pt-3" : ""}`}
      >
        <Icon className="h-4 w-4 text-[var(--text-tertiary)]" />
        <span
          className={
            sc.step_type === "heading"
              ? "text-sm font-semibold text-[var(--text-primary)]"
              : "text-xs text-[var(--text-secondary)] italic"
          }
        >
          {sc.label}
        </span>
      </div>
    );
  }

  // Completed step
  if (sc.is_completed) {
    const displayValue =
      sc.value?.response != null ? String(sc.value.response) : "Done";
    return (
      <div className="flex items-center gap-3 py-1.5">
        <CheckCircle className="h-4 w-4 text-[var(--text-success)]" />
        <span className="flex-1 text-sm text-[var(--text-primary)]">
          {sc.label}
        </span>
        <span className="text-xs font-medium text-[var(--text-success)]">
          {displayValue}
        </span>
      </div>
    );
  }

  // Checkbox — single click to complete
  if (sc.step_type === "checkbox") {
    return (
      <button
        onClick={() => completeMutation.mutate(true)}
        className="flex w-full items-center gap-3 py-1.5 text-left hover:bg-[var(--surface-base)] rounded-[var(--radius-sm)] px-1 -mx-1 transition-colors"
      >
        <Circle className="h-4 w-4 text-[var(--text-tertiary)]" />
        <span className="flex-1 text-sm text-[var(--text-primary)]">
          {sc.label}
        </span>
      </button>
    );
  }

  // Inspection — Pass/Flag/Fail buttons
  if (sc.step_type === "inspection") {
    return (
      <div className="flex items-center gap-3 py-1.5">
        <Circle className="h-4 w-4 text-[var(--text-tertiary)]" />
        <span className="flex-1 text-sm text-[var(--text-primary)]">
          {sc.label}
        </span>
        <div className="flex gap-1">
          {["pass", "flag", "fail"].map((v) => (
            <button
              key={v}
              onClick={() => completeMutation.mutate(v)}
              className={`rounded-[var(--radius-md)] px-2.5 py-1 text-[11px] font-medium border transition-colors ${
                v === "pass"
                  ? "border-[var(--text-success)]/30 text-[var(--text-success)] hover:bg-[var(--fill-success)]"
                  : v === "flag"
                    ? "border-[var(--text-warning)]/30 text-[var(--text-warning)] hover:bg-[var(--fill-warning)]"
                    : "border-[var(--text-danger)]/30 text-[var(--text-danger)] hover:bg-[var(--fill-danger)]"
              }`}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Yes/No/N/A
  if (sc.step_type === "yes_no_na") {
    return (
      <div className="flex items-center gap-3 py-1.5">
        <Circle className="h-4 w-4 text-[var(--text-tertiary)]" />
        <span className="flex-1 text-sm text-[var(--text-primary)]">
          {sc.label}
        </span>
        <div className="flex gap-1">
          {["yes", "no", "na"].map((v) => (
            <button
              key={v}
              onClick={() => completeMutation.mutate(v)}
              className="rounded-[var(--radius-md)] border border-[var(--border-default)] px-2.5 py-1 text-[11px] font-medium text-[var(--text-secondary)] hover:border-[var(--color-interactive-primary)]/30 hover:text-[var(--color-interactive-primary)] transition-colors"
            >
              {v === "na" ? "N/A" : v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // All other types — click to show input, then submit
  return (
    <div className="py-1.5">
      <div className="flex items-center gap-3">
        <Circle className="h-4 w-4 text-[var(--text-tertiary)]" />
        <span className="flex-1 text-sm text-[var(--text-primary)]">
          {sc.label}
        </span>
        {!showInput && (
          <button
            onClick={() => setShowInput(true)}
            className="text-xs text-[var(--color-interactive-primary)] font-medium hover:underline"
          >
            Enter
          </button>
        )}
      </div>
      {showInput && (
        <div className="ml-7 mt-1.5 flex gap-2">
          <input
            type={
              sc.step_type === "number" ||
              sc.step_type === "amount" ||
              sc.step_type === "meter_reading"
                ? "number"
                : sc.step_type === "date"
                  ? "date"
                  : "text"
            }
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={sc.label}
            step={
              sc.step_type === "amount"
                ? "0.01"
                : sc.step_type === "number"
                  ? "any"
                  : undefined
            }
            autoFocus
            className="flex-1 h-8 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
          />
          <Button
            size="sm"
            onClick={() => {
              const parsed =
                sc.step_type === "number" || sc.step_type === "meter_reading"
                  ? parseFloat(value) || 0
                  : sc.step_type === "amount"
                    ? parseFloat(value) || 0
                    : value;
              completeMutation.mutate(parsed);
            }}
            isLoading={completeMutation.isPending}
          >
            <Check className="h-3 w-3" />
          </Button>
          <button
            onClick={() => {
              setShowInput(false);
              setValue("");
            }}
            className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default ProcessRunner;
